import React from 'react';
import { docketDataConverter, integrationDataLabels, sliceString } from '../../../helpers';
import { PlusIcon } from '../../../components/SVGIcons/SVGIcons';
import { useNavigate } from 'react-router';

const BACKUP_KEY = '_originalBeforeFieldsMigration';
const INCLUDED_PREFIX = 'included_field_';

function renderDocketValue(value, timeSelect) {
	const converted = docketDataConverter(value, timeSelect);

	if (
		converted == null ||
		typeof converted === 'string' ||
		typeof converted === 'number' ||
		React.isValidElement(converted)
	) {
		return converted ?? '';
	}

	try {
		return JSON.stringify(converted);
	} catch {
		return String(converted);
	}
}

function buildDisplayFields(customFields, docketFields) {
	if (!customFields) return [];

	const cf = customFields;
	const hasNewStructure = cf.fields && typeof cf.fields === 'object';

	const docketDataFilter = [
		'docketName',
		'docketUuid',
		'integrationMaps',
		'requiredDateTime',
		'fields',
		'includedFields',
		BACKUP_KEY,
		'daterequired',
		'dateRequired',
	];

	const getLabel = (key, overrideFieldText) =>
		integrationDataLabels('dentally')[key] || overrideFieldText || docketFields?.[key]?.fieldText || key;

	const fields = [];

	if (hasNewStructure) {
		// New format: customFields.fields + includedFields
		Object.keys(cf.fields).forEach((fieldName) => {
			const fieldCfg = cf.fields[fieldName] || {};
			const value = fieldCfg.value;

			fields.push({
				key: fieldName,
				value,
				isIncluded: false,
				label: getLabel(fieldName, fieldCfg.fieldText),
				timeSelect: fieldCfg.timeSelect ?? docketFields?.[fieldName]?.timeSelect,
			});
		});

		if (cf.includedFields && typeof cf.includedFields === 'object') {
			Object.keys(cf.includedFields).forEach((includedKey) => {
				fields.push({
					key: includedKey,
					value: cf.includedFields[includedKey],
					isIncluded: true,
					label: getLabel(includedKey),
					timeSelect: docketFields?.[includedKey]?.timeSelect,
				});
			});
		}
	} else {
		// Old format: flat keys on customFields
		Object.keys(cf).forEach((key) => {
			if (docketDataFilter.includes(key)) return;

			const isIncluded = key.startsWith(INCLUDED_PREFIX);
			const value = cf[key];

			fields.push({
				key,
				value,
				isIncluded,
				label: getLabel(key),
				timeSelect: docketFields?.[key]?.timeSelect,
			});
		});
	}

	return fields;
}

function buildChangeReqDisplayFields(change, changeReqFields) {
	if (!change) return [];

	// Prefer snapshot data if it looks like a modern shape
	const snapshot = change.snapshot && typeof change.snapshot === 'object' ? change.snapshot : null;

	let cf = null;
	if (snapshot && snapshot.customFields && typeof snapshot.customFields === 'object') {
		// Newer snapshots storing customFields like the order
		cf = snapshot.customFields;
	} else if (snapshot && snapshot.fields && typeof snapshot.fields === 'object') {
		// Snapshot directly has fields
		cf = snapshot;
	} else if (change.customFields && typeof change.customFields === 'object') {
		// Fallback: change itself has customFields
		cf = change.customFields;
	} else {
		// Legacy V1: treat the entire change object as flat data
		cf = change;
	}

	const hasNewStructure = cf.fields && typeof cf.fields === 'object';

	const docketDataFilter = [
		'docketName',
		'docketUuid',
		'integrationMaps',
		'requiredDateTime',
		'fields',
		'includedFields',
		BACKUP_KEY,
		'daterequired',
		'dateRequired',
		'createdAt',
	];

	const getLabel = (key, overrideFieldText) => changeReqFields?.[key]?.fieldText || overrideFieldText || key;

	const fields = [];

	if (hasNewStructure) {
		// New-style change snapshot
		Object.keys(cf.fields).forEach((fieldName) => {
			const fieldCfg = cf.fields[fieldName] || {};
			const value = fieldCfg.value;

			fields.push({
				key: fieldName,
				value,
				isIncluded: false,
				label: getLabel(fieldName, fieldCfg.fieldText),
				timeSelect: fieldCfg.timeSelect,
			});
		});

		if (cf.includedFields && typeof cf.includedFields === 'object') {
			Object.keys(cf.includedFields).forEach((includedKey) => {
				fields.push({
					key: includedKey,
					value: cf.includedFields[includedKey],
					isIncluded: true,
					label: getLabel(includedKey),
					timeSelect: undefined,
				});
			});
		}
	} else {
		// Legacy flat change object (old format in changeReqHistory array)
		Object.keys(cf).forEach((key) => {
			if (docketDataFilter.includes(key)) return;

			const isIncluded = key.startsWith(INCLUDED_PREFIX);
			const value = cf[key];

			fields.push({
				key,
				value,
				isIncluded,
				label: getLabel(key),
				timeSelect: undefined,
			});
		});
	}

	return fields;
}

export default function OrderItems(props) {
	const { order, addons, isSeller, changeReqUuid, docketFields, changeReqFields, changeRequests } =
		props?.props || {};

	const navigate = useNavigate();

	const displayFields = React.useMemo(
		() => buildDisplayFields(order?.customFields, docketFields),
		[order?.customFields, docketFields]
	);

	const changeRequestsToRender = React.useMemo(() => {
		// Prefer explicit changeRequests prop if provided
		if (Array.isArray(changeRequests) && changeRequests.length) {
			return changeRequests;
		}

		// Fallback: legacy array on the order object
		if (Array.isArray(order?.changeReqHistory) && order.changeReqHistory.length) {
			return order.changeReqHistory;
		}

		return [];
	}, [changeRequests, order?.changeReqHistory]);

	return (
		<div className='gridItemOrder items'>
			<div className='row highlight'>
				<h4 className='gridHeader'>Items</h4>
				{!isSeller && (
					<div
						className={`gridIconRegular discIcon${!changeReqUuid ? ' disabledSVG' : ''}`}
						onClick={() => {
							if (!changeReqUuid) return;
							navigate(
								`/order?changeReqUuid=${changeReqUuid}&uuid=${order.sellerTeamUuid}&name=${
									order.sellerTeamName
								}&changeReqOrderUuid=${order.orderUuid}&returnUri=${encodeURI(
									`/orders/${order.orderUuid}?orderRef=${order.orderRef}`
								)}`
							);
						}}>
						<PlusIcon />
					</div>
				)}
			</div>
			<div className='colCont'>
				<div className='row header'>
					<div className='rowItem'>
						<h4 className='subHeader'>PRESCRIPTION</h4>
					</div>
					<div className='rowItem'>
						<h4 className='subHeader'>PROPERTIES</h4>
					</div>
					<div className='rowItem'>
						<h4 className='subHeader'>PRICE</h4>
					</div>
				</div>
				<div className='col'>
					<div className='row data'>
						<div className='rowItem'>
							<h4 className='subHeader'>PRESCRIPTION</h4>
							<div className='segmentText'>
								{order?.customFields?.docketName &&
								typeof order.customFields.docketName === 'string' ? (
									<p className='bodyText'>{order.customFields.docketName}</p>
								) : (
									<p className='bodyText'>None</p>
								)}
							</div>
						</div>
						<div className='rowItem'>
							<h4 className='subHeader'>PROPERTIES</h4>
							{order?.customFields ? (
								displayFields.length ? (
									displayFields.map((field) => (
										<div className='row marginBottom-6' key={field.key}>
											{!field.isIncluded && <p className='text'>{field.label}: </p>}
											<div
												className='data bold'
												style={field.isIncluded ? { width: '100%', marginTop: '12px' } : {}}>
												{renderDocketValue(field.value, field.timeSelect)}
											</div>
										</div>
									))
								) : (
									<p>No docket data</p>
								)
							) : (
								<p>No docket data</p>
							)}
						</div>
						<div className='rowItem'>
							<h4 className='subHeader'>PRICE</h4>
							<div className='segmentText'>
								<div className='data bold'>
									{order?.totalPrice ? `${Number(order.totalPrice).toFixed(2)}£` : '0.00£'}
								</div>
							</div>
						</div>
					</div>

					{changeRequestsToRender.map((changeRaw, idx) => {
						const change = changeRaw || {};

						const changeFields = buildChangeReqDisplayFields(change, changeReqFields);

						// Prefer docketName from the snapshot/customFields if the top-level is missing
						const changeDocketName =
							change.docketName ||
							change?.customFields?.docketName ||
							change?.snapshot?.customFields?.docketName ||
							change?.snapshot?.docketName ||
							null;

						// Handle createdAt for both legacy and new API
						const createdAt =
							change.createdAt ||
							change._createdAt ||
							change.timestamp ||
							change.snapshot?.createdAt ||
							null;

						const key =
							change.changeRequestUuid ||
							change.changeReqUuid ||
							change.uuid ||
							change._id ||
							`cr-${idx}`;

						return (
							<div className='row data borderTop' key={key}>
								<div className='rowItem'>
									<h4 className='subHeader'>PRESCRIPTION</h4>
									<div className='segmentText'>
										{changeDocketName && typeof changeDocketName === 'string' ? (
											<p className='bodyText'>{changeDocketName}</p>
										) : (
											<p className='bodyText'>None</p>
										)}
									</div>
								</div>
								<div className='rowItem'>
									<h4 className='subHeader'>PROPERTIES</h4>

									{changeFields.length ? (
										changeFields.map((field) => (
											<div className='row' key={field.key}>
												{!field.isIncluded && <p className='text'>{field.label}: </p>}
												<div className='data bold'>
													{renderDocketValue(field.value, field.timeSelect)}
												</div>
											</div>
										))
									) : (
										<p>No change request data</p>
									)}

									<div className='row' key={`${key}-createdAt`}>
										<p className='text'>Date submitted: </p>
										<div className='data bold'>{renderDocketValue(createdAt, undefined)}</div>
									</div>
								</div>
								<div className='rowItem'>
									<h4 className='subHeader'>PRICE</h4>
									<div className='segmentText'>
										<div className='data bold'>
											{order?.totalPrice ? `${Number(order.totalPrice).toFixed(2)}£` : '0.00£'}
										</div>
									</div>
								</div>
							</div>
						);
					})}

					{addons && (
						<div className='row'>
							<div className='rowItem'>
								<h4 className='subHeader'>ADD-ONS</h4>
							</div>
							<div className='rowItem'>
								<h4 className='subHeader'>QUANTITY</h4>
							</div>
							<div className='rowItem' />
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
